/**
 * Extract a still frame from a recorded video Blob (TICKET-281c, hardened in TICKET-281h).
 *
 * Used to feed the recognition pipeline a JPEG when the user records
 * a video proof rather than capturing a still photo.
 *
 * @module services/videoFrameExtractor
 */

const SERVICE_TAG = '[VIDEO_FRAME]';

/** Max time to wait for the video element to become readable. */
const READY_TIMEOUT_MS = 4000;
/** Max time to wait for `seeked` to fire after setting currentTime. */
const SEEK_TIMEOUT_MS = 500;

/**
 * Decode `blob`, seek toward the end of the clip, and return that frame as a
 * base64 JPEG data URL. Resolves the object URL on both success and failure
 * paths. The seek target is `min(max(duration - 0.2, 0), 1.5)` — the settle
 * path guarantees a stable tail, so the last frame is by construction the
 * settled frame (TICKET-293).
 *
 * Browser quirks handled:
 *  - Some browsers (notably mobile Safari on user-recorded blobs) require
 *    `video.load()` to be called explicitly after `src` is set, otherwise
 *    `loadedmetadata`/`loadeddata` may never fire.
 *  - We listen for both `loadedmetadata` and `loadeddata` because some
 *    MediaRecorder outputs deliver only one of them.
 *  - If the video is already at `readyState >= HAVE_CURRENT_DATA` when we
 *    attach, we resolve immediately to avoid waiting for an event that
 *    will never fire.
 *  - iOS Safari sometimes never fires `seeked` after a `currentTime` set; a
 *    {@link SEEK_TIMEOUT_MS} timeout falls through and lets the canvas grab
 *    whatever the video's already-decoded current frame is — which on a
 *    settle-driven recording is also near the end.
 *  - On decode failure, surfaces `video.error.code` and the blob's MIME type
 *    so the failure mode is diagnosable from logs alone.
 *
 * @param blob - Recorded video blob.
 * @param qualityHint - JPEG quality 0-1 (default 0.9).
 * @returns Data URL string `data:image/jpeg;base64,...`
 */
export async function extractFirstFrame(blob: Blob, qualityHint = 0.9): Promise<string> {
  console.info(`${SERVICE_TAG} Extracting first frame`, {
    blobType: blob.type || '(empty)',
    blobSize: blob.size
  });

  const url = URL.createObjectURL(blob);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    // Some Safari versions decode user-recorded blobs more reliably when the
    // element is attached to the DOM. We mount it offscreen as a defence;
    // if any of this fails (e.g. headless test environments without a real
    // Node interface), we proceed with a detached element — that path works
    // in every browser we've shipped against today.
    let mounted = false;
    try {
      video.style.position = 'fixed';
      video.style.left = '-9999px';
      video.style.top = '-9999px';
      video.style.width = '1px';
      video.style.height = '1px';
      document.body.appendChild(video);
      mounted = true;
    } catch {
      mounted = false;
    }

    try {
      video.src = url;
      // Explicit load() trigger — without this, some browsers never start
      // decoding when src is a Blob URL.
      video.load();

      await waitForReadable(video, blob);

      // Seek toward end of clip — settle path guarantees a stable tail
      // (motion controller's stabilityDurationMs=1500 then stopNow()), so
      // the last ~0.2s is by construction the settled frame, not the
      // motion-blurred press-shake at t=0 (TICKET-293). Clamp so very short
      // clips never go negative and never overshoot 1.5s of tail.
      try {
        video.currentTime = Math.min(Math.max(video.duration - 0.2, 0), 1.5);
      } catch {
        // Some browsers throw if the element is not yet ready to seek;
        // ignore — we'll grab whatever frame is current.
      }

      await new Promise<void>((resolve) => {
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          resolve();
        };
        video.addEventListener('seeked', onSeeked);
        setTimeout(resolve, SEEK_TIMEOUT_MS);
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context unavailable');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', qualityHint);
      console.info(`${SERVICE_TAG} Extracted first frame`, {
        width: canvas.width,
        height: canvas.height,
        bytes: dataUrl.length
      });
      return dataUrl;
    } finally {
      // Always remove the offscreen element regardless of success/failure,
      // but only if we actually managed to mount it.
      if (mounted) {
        try { video.remove(); } catch { /* noop */ }
      }
    }
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Wait for the video element to reach a readable state, or for an error event.
 * Resolves on `loadedmetadata`/`loadeddata`/`canplay` (whichever fires first),
 * or if `readyState` is already >= HAVE_CURRENT_DATA when called. Rejects on
 * the `error` event with a diagnosable message that includes the underlying
 * `MediaError.code` and the blob's MIME type.
 */
function waitForReadable(video: HTMLVideoElement, blob: Blob): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    // HAVE_CURRENT_DATA = 2 — enough to render the current frame.
    if (video.readyState >= 2) {
      resolve();
      return;
    }

    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const onReady = () => settle(resolve);
    const onError = () => {
      const code = video.error?.code ?? 'unknown';
      const message = video.error?.message ?? '';
      settle(() => reject(new Error(
        `Video decode failed (code=${code} blobType=${blob.type || 'empty'} blobSize=${blob.size}${message ? ` msg=${message}` : ''})`
      )));
    };
    const onTimeout = () => settle(() => reject(new Error(
      `Video decode timed out after ${READY_TIMEOUT_MS}ms (readyState=${video.readyState} blobType=${blob.type || 'empty'} blobSize=${blob.size})`
    )));

    const timer = setTimeout(onTimeout, READY_TIMEOUT_MS);
    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('error', onError);
    };

    video.addEventListener('loadedmetadata', onReady);
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('canplay', onReady);
    video.addEventListener('error', onError);
  });
}
